/**
 * The whole-record relationship GRAPH: its model, and its three layouts —
 * Network and Metro (#2324), and Levels, the leading design's (#2406).
 *
 * ── WHY THIS IS NOT IN THE COMPONENT ───────────────────────────────────────
 *
 * Everything here is a pure function of a governed answer. `core.relationship_graph_hop`
 * decides what is in the graph and who may see it; these decide only where it
 * SITS. Keeping the arithmetic out of the renderer is what makes the ruling's
 * hardest clause testable at all:
 *
 *   "Switching Network ↔ Metro changes coordinates/routing only. Loaded nodes,
 *    expansion state, selected node, graph root, and authorization result stay
 *    unchanged."
 *
 * That is a statement about two FUNCTIONS OVER ONE MODEL, and it is asserted as
 * one. Written inside a component it would have been a statement about a
 * re-render, which is the version that passes while the data is quietly rebuilt.
 *
 * ── WHAT IS DELIBERATELY ABSENT ────────────────────────────────────────────
 *
 *   · no fetching, no authority, no entity knowledge. A node the caller may not
 *     read never arrives here, and nothing here could tell.
 *   · no persisted coordinates. §8 of the ruling: a hand-arranged position is
 *     not platform data. Both layouts recompute from the model every time,
 *     which is also what makes them deterministic.
 *   · no colour. A line's IDENTITY is an index; which paint an index becomes is
 *     the host's, and §7 requires that the meaning never be carried by colour
 *     alone — so the index also drives a dash pattern and a label.
 */

/** One record in the loaded graph. `key` is `<entity>:<id>`, the door's own. */
export interface GraphNode {
  key: string
  entity: string
  id: string
  /** The governed display label, or null where the caller may not name it. */
  label: string | null
  subtitle?: string | null
}

/** One typed relationship instance. `source`/`target` are node keys or `root`. */
export interface GraphEdge {
  key: string
  slot: string
  relType: string
  label: string
  source: string
  target: string
  /** Only where the relationship declares a meaningful reading. */
  directed: boolean
  detail?: Record<string, unknown>
}

/** One governed relationship group of one record. */
export interface GraphGroup {
  slot: string
  relType: string
  direction: string
  target: string
  label: string
  targetLabel?: string | null
  /** The caller's own authorized count. `null` where the door did not measure. */
  count: number | null
  capped: boolean
  loaded: number
  more: boolean
  cursor: number | null
  directed: boolean
}

/** One answer from `core.relationship_graph_hop`, as it arrives. */
export interface GraphHop {
  root: GraphNode
  nodes: GraphNode[]
  edges: GraphEdge[]
  groups: GraphGroup[]
  refusals: { slot: string; reason: string }[]
  limits?: { pageSize?: number }
}

/**
 * The LOADED graph: every hop the person has asked for, merged.
 *
 * `depth` is the shortest hop count from the root, and it is DERIVED here by a
 * breadth-first walk rather than carried on the wire. The door answers one hop
 * at a time and does not know what else is on screen; a depth stamped by the
 * door would be "distance from whichever record you expanded", which is a
 * different number and is wrong the moment the same record is reached twice.
 */
export interface GraphModel {
  /** The record the graph is about. Always at depth 0, always drawn distinctly. */
  root: GraphNode
  /** Every loaded node, root included, keyed by node key. */
  nodes: Map<string, GraphNode & { depth: number }>
  edges: GraphEdge[]
  /** Groups of the ROOT. A child's groups arrive with its own expansion. */
  groups: GraphGroup[]
  /** Which node each expansion was rooted at, in the order asked. */
  expanded: string[]
  refusals: { slot: string; reason: string }[]
}

const ROOT = 'root'

/**
 * Merge hops into one loaded graph.
 *
 * The FIRST hop's root is the graph's root; later hops are expansions of a node
 * already in it. A node reached twice keeps its first identity and its shallower
 * depth, and every edge survives — §3 of the ruling: "the same record reached
 * through multiple relationships is one node with multiple typed edges, not
 * duplicated identities."
 *
 * Cycles terminate because this is a SET union and not a walk: an edge back to
 * the root is one more edge on a node that already exists.
 */
export function buildGraphModel(hops: readonly GraphHop[]): GraphModel {
  const first = hops[0]
  if (!first) {
    return {
      root: { key: ROOT, entity: '', id: '', label: null },
      nodes: new Map(), edges: [], groups: [], expanded: [], refusals: [],
    }
  }
  const root: GraphNode = { ...first.root, key: ROOT }
  const nodes = new Map<string, GraphNode & { depth: number }>()
  nodes.set(ROOT, { ...root, depth: 0 })

  /* The root answers to TWO keys and they must collapse to one.
   *
   * The first hop calls it `root`; every LATER hop that reaches it calls it
   * `<entity>:<id>`, because to that hop it is an ordinary neighbour. Found by
   * the cycle witness: expanding a child that links back to the record you are
   * standing on drew the record TWICE — once at the centre and once as a
   * station — which is the "duplicated identities" §3 forbids, in the one place
   * a graph makes a fabrication look authoritative. */
  const rootKey = `${root.entity}:${root.id}`
  const canonical = (key: string) => (key === rootKey ? ROOT : key)

  const edges: GraphEdge[] = []
  const edgeKeys = new Set<string>()
  const expanded: string[] = []
  const refusals: { slot: string; reason: string }[] = []

  for (const hop of hops) {
    // A hop's own root is `root` in ITS answer; in the merged graph it is
    // whichever node it actually is. The first hop defines the graph root, so
    // only later hops need rewriting.
    const hopRootKey = hops.indexOf(hop) === 0
      ? ROOT
      : canonical(`${hop.root.entity}:${hop.root.id}`)
    if (hopRootKey !== ROOT && !expanded.includes(hopRootKey)) expanded.push(hopRootKey)
    const rename = (endpoint: string) =>
      canonical(endpoint === ROOT ? hopRootKey : endpoint)

    for (const node of hop.nodes) {
      const key = canonical(node.key)
      if (!nodes.has(key)) nodes.set(key, { ...node, key, depth: Number.MAX_SAFE_INTEGER })
    }
    for (const edge of hop.edges) {
      const key = `${edge.key}@${hopRootKey}`
      if (edgeKeys.has(key)) continue
      edgeKeys.add(key)
      edges.push({ ...edge, key, source: rename(edge.source), target: rename(edge.target) })
    }
    for (const refusal of hop.refusals ?? []) {
      if (!refusals.some((r) => r.slot === refusal.slot)) refusals.push(refusal)
    }
  }

  // Depth by breadth-first walk over the merged edge set. Undirected on purpose:
  // "how many steps from the record I am looking at" is a question about the
  // picture, and an inbound edge is one step exactly as an outbound one is.
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    for (const [from, to] of [[edge.source, edge.target], [edge.target, edge.source]] as const) {
      adjacency.set(from, [...(adjacency.get(from) ?? []), to])
    }
  }
  const queue: string[] = [ROOT]
  for (let at = 0; at < queue.length; at += 1) {
    const here = queue[at]!
    const depth = nodes.get(here)?.depth ?? 0
    for (const next of adjacency.get(here) ?? []) {
      const node = nodes.get(next)
      if (!node || node.depth <= depth + 1) continue
      nodes.set(next, { ...node, depth: depth + 1 })
      queue.push(next)
    }
  }
  // A node nothing reached keeps a finite depth rather than MAX_SAFE_INTEGER —
  // it is loaded, so it is drawn, and a coordinate computed from an unbounded
  // number is off the canvas rather than merely in the wrong place.
  for (const [key, node] of nodes) {
    if (node.depth === Number.MAX_SAFE_INTEGER) nodes.set(key, { ...node, depth: 1 })
  }

  /* The GROUPS come from the LAST hop that is about the root, not the first.
   *
   * "Show more" is another hop with the same root and a cursor, and its answer
   * carries the group's NEW `more`/`cursor`. Taking the first hop's groups left
   * a paged group permanently offering the page it had already loaded — found by
   * the browser proof, where clicking Show more twice asked for the same
   * records and the node count never moved. */
  const latest = [...hops].reverse().find((hop) =>
    `${hop.root.entity}:${hop.root.id}` === rootKey) ?? first
  return { root, nodes, edges, groups: latest.groups ?? [], expanded, refusals }
}

/* ── LINE IDENTITY ──────────────────────────────────────────────────────────
 *
 * A relationship type is a LINE, in both layouts. Its identity is an index, and
 * the index is assigned from the governed group order rather than from a hash:
 * a hash is stable across renders and unstable across deployments, so two
 * screenshots of the same fixture would differ for no reason anybody could see.
 *
 * §7 forbids meaning carried by colour alone, so the index is meant to drive
 * MORE than a paint — the host maps it to a stroke pattern and the legend names
 * it. That mapping is the host's; the number is here so both layouts, the
 * legend and the accessible list agree about which line is which.
 */
export function lineIndexes(groups: readonly GraphGroup[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const group of groups) if (!out.has(group.slot)) out.set(group.slot, out.size)
  return out
}

export interface Point { x: number; y: number }
export interface GraphPlacement {
  /** Where each node sits, keyed by node key. The root is always present. */
  nodes: Map<string, Point>
  /** The polyline each edge is drawn as — two points for a straight run. */
  edges: Map<string, Point[]>
  /** The drawing area the placement needs, in the same units. */
  bounds: { width: number; height: number }
  /** Which layout produced it. Carried so a host cannot mislabel a picture. */
  layout: 'network' | 'metro'
}

export interface LayoutOptions {
  /** Distance between one depth ring and the next. */
  radius?: number
  /** Distance between two stations on one metro line. */
  step?: number
  /** Padding around the outermost node. */
  pad?: number
}

const DEFAULTS = { radius: 150, step: 110, pad: 60 }

/** Every loaded node except the root, in the governed order the edges imply. */
function orderedNodes(model: GraphModel): { key: string; depth: number; slot: string }[] {
  const firstEdge = new Map<string, GraphEdge>()
  for (const edge of model.edges) {
    for (const endpoint of [edge.source, edge.target]) {
      if (endpoint !== ROOT && !firstEdge.has(endpoint)) firstEdge.set(endpoint, edge)
    }
  }
  const lines = lineIndexes(model.groups)
  return [...model.nodes.entries()]
    .filter(([key]) => key !== ROOT)
    .map(([key, node]) => ({
      key,
      depth: node.depth,
      slot: firstEdge.get(key)?.slot ?? '',
    }))
    .sort((a, b) => a.depth - b.depth
      || (lines.get(a.slot) ?? Number.MAX_SAFE_INTEGER) - (lines.get(b.slot) ?? Number.MAX_SAFE_INTEGER)
      || a.slot.localeCompare(b.slot)
      || a.key.localeCompare(b.key))
}

const round = (n: number): number => Math.round(n * 100) / 100

function framed(points: Map<string, Point>, pad: number): {
  nodes: Map<string, Point>; bounds: { width: number; height: number }
} {
  let minX = 0; let minY = 0; let maxX = 0; let maxY = 0
  for (const p of points.values()) {
    minX = Math.min(minX, p.x); minY = Math.min(minY, p.y)
    maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y)
  }
  const shifted = new Map<string, Point>()
  for (const [key, p] of points) {
    shifted.set(key, { x: round(p.x - minX + pad), y: round(p.y - minY + pad) })
  }
  return {
    nodes: shifted,
    bounds: { width: round(maxX - minX + pad * 2), height: round(maxY - minY + pad * 2) },
  }
}

/**
 * NETWORK — the conventional relationship graph.
 *
 * Concentric rings by depth, the root at the centre, and within a ring the
 * governed order: line index, then slot, then node key. DETERMINISTIC on
 * purpose — §5 of the ruling asks for stable placement and no movement after
 * load, and a force simulation is the opposite of both. The same model always
 * produces the same picture, which is also what makes a raster comparable.
 *
 * Edges are straight runs, because at this density they are legible and an
 * arbitrary curve is decoration that can only make a crossing harder to read.
 */
export function networkLayout(model: GraphModel, options: LayoutOptions = {}): GraphPlacement {
  const { radius, pad } = { ...DEFAULTS, ...options }
  const points = new Map<string, Point>([[ROOT, { x: 0, y: 0 }]])
  const byDepth = new Map<number, { key: string }[]>()
  for (const node of orderedNodes(model)) {
    byDepth.set(node.depth, [...(byDepth.get(node.depth) ?? []), node])
  }
  for (const [depth, ring] of [...byDepth.entries()].sort((a, b) => a[0] - b[0])) {
    const r = radius * depth
    ring.forEach((node, i) => {
      // The first node of every ring sits at twelve o'clock, so a one-node ring
      // is directly above the root rather than at whatever angle a zero index
      // happens to produce.
      const angle = (i / ring.length) * Math.PI * 2 - Math.PI / 2
      points.set(node.key, { x: Math.cos(angle) * r, y: Math.sin(angle) * r })
    })
  }
  const { nodes, bounds } = framed(points, pad)
  const edges = new Map<string, Point[]>()
  for (const edge of model.edges) {
    const from = nodes.get(edge.source)
    const to = nodes.get(edge.target)
    if (from && to) edges.set(edge.key, [from, to])
  }
  return { nodes, edges, bounds, layout: 'network' }
}

/* ── METRO ─────────────────────────────────────────────────────────────────
 *
 * A schematic, and the ruling states its rules: routed horizontal, vertical and
 * 45-degree segments; relationship types as visually consistent lines; shared
 * records as interchanges; crossings and labels distinguishable.
 *
 * The eight compass directions, in the order lines take them. Chosen so the
 * first four lines are the cardinal ones — the easiest to read — and the
 * diagonals fill in after. A line's direction is its INDEX, so it is the same
 * on every render and on every deployment.
 */
const COMPASS: Point[] = [
  { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 },
  { x: 1, y: 1 }, { x: -1, y: 1 }, { x: -1, y: -1 }, { x: 1, y: -1 },
]

/**
 * Route one segment as a metro map does: horizontal, vertical, or 45°.
 *
 * Two points that already lie on one of those three lines are a straight run.
 * Anything else becomes TWO segments — a 45° diagonal for as far as it can go,
 * then a straight run for the remainder. That is the classic octilinear route,
 * and it is what keeps an interchange reachable from a line whose direction does
 * not point at it without drawing an arbitrary curve.
 */
export function octilinear(from: Point, to: Point): Point[] {
  const dx = to.x - from.x
  const dy = to.y - from.y
  if (dx === 0 || dy === 0 || Math.abs(dx) === Math.abs(dy)) return [from, to]
  const run = Math.min(Math.abs(dx), Math.abs(dy))
  const knee: Point = {
    x: round(from.x + Math.sign(dx) * run),
    y: round(from.y + Math.sign(dy) * run),
  }
  return [from, knee, to]
}

/**
 * METRO — the same nodes and edges, placed as a schematic.
 *
 * Each LINE (a governed relationship group) takes a compass direction by index
 * and lays its stations along it at a fixed step. A node already placed by an
 * earlier line is an INTERCHANGE: it keeps its position, and the later line
 * routes to it octilinearly rather than claiming it — which is what makes an
 * interchange one node with several lines through it rather than two dots.
 *
 * Depth pushes a station outward along its own line, so an expanded child sits
 * beyond its parent on the same track. A second hop does not get a second
 * direction: the line is the relationship type, not the distance.
 */
export function metroLayout(model: GraphModel, options: LayoutOptions = {}): GraphPlacement {
  const { step, pad } = { ...DEFAULTS, ...options }
  const lines = lineIndexes(model.groups)
  const points = new Map<string, Point>([[ROOT, { x: 0, y: 0 }]])

  // How far along each line the next station goes. Per DIRECTION rather than
  // per line, so two lines sharing a compass direction (more groups than there
  // are directions) do not stack their stations on top of each other.
  const taken = new Map<number, number>()
  const ordered = orderedNodes(model)
  for (const node of ordered) {
    if (points.has(node.key)) continue
    const line = lines.get(node.slot) ?? lines.size
    const dir = line % COMPASS.length
    const lane = Math.floor(line / COMPASS.length)
    const at = (taken.get(dir) ?? 0) + 1
    taken.set(dir, at)
    const compass = COMPASS[dir]!
    // The LANE offset is perpendicular to the line and is what keeps a ninth
    // relationship type off the top of the first one. It is a whole step, so
    // the grid stays octilinear.
    points.set(node.key, {
      x: round(compass.x * step * at + compass.y * step * lane * 0.5),
      y: round(compass.y * step * at - compass.x * step * lane * 0.5),
    })
  }

  const { nodes, bounds } = framed(points, pad)
  const edges = new Map<string, Point[]>()
  for (const edge of model.edges) {
    const from = nodes.get(edge.source)
    const to = nodes.get(edge.target)
    if (from && to) edges.set(edge.key, octilinear(from, to))
  }
  return { nodes, edges, bounds, layout: 'metro' }
}

/* ── LEVELS (#2406) ────────────────────────────────────────────────────────
 *
 * The leading design's Connections Graph, as a pure function of the same model.
 * The record sits on the left; one COLUMN per hop — level 1 is the record's own
 * relationships, level 2 is what an expansion brought in, and so on; and one
 * BAND per governed relationship group of the record, in the governed order, so
 * a band is the answer to "what is this record's People?" with everything that
 * hangs off each person drawn beside them.
 *
 * It is a TREE drawn over a graph, and the two places those disagree are the
 * two decisions this function makes:
 *
 *   · a record reached by TWO relationships is still ONE card (§3: "one node
 *     with multiple typed edges, not duplicated identities"). The card sits in
 *     the first band the governed order reaches it through; every other band
 *     that reaches it carries a REFERENCE row naming it and saying where it is
 *     drawn. A second card would read as a second record, and dropping the row
 *     would make the second band's count disagree with what it shows.
 *   · an expansion that reaches back to the record itself draws nothing new.
 *     The record's own hop already carries every one of its relationships, so
 *     the back-edge is a relationship already on screen.
 *
 * Positions are deterministic — no simulation, nothing persisted — and depend on
 * the model, the hidden groups, the card heights and the one arrangement choice
 * (`compact`), which a host makes from the width it has, never from what loaded.
 * Changing what loaded never switches arrangement under the reader.
 */

export interface LevelsRect { x: number; y: number; width: number; height: number }

/** One placed record, or one reference to a record already placed elsewhere. */
export interface LevelsItem extends LevelsRect {
  kind: 'card' | 'ref'
  /** The node key. A `ref` shares it with the card it points at. */
  key: string
  /** Hop depth from the root: 1 is a direct relationship, 2 and on are nested. */
  level: number
  /** The slot of the ROOT group whose band this sits in. */
  band: string
  /** `root`, or the expanded node that brought this one in. */
  parent: string
  /** The edge that put it here — whose facts a card shows. */
  via: string
  /** For a `ref`: the band the node's card is in. */
  home?: string
}

export interface LevelsBand extends LevelsRect {
  slot: string
  /** The group's line index — the same identity the other layouts and the legend use. */
  line: number
  /** The "show more" row, where the door said there is more. */
  more: LevelsRect | null
  /** A band with nothing loaded and nothing to page says so in this row. */
  empty: LevelsRect | null
}

export interface LevelsWire {
  /** The edge key — one wire per placing edge, never one per node. */
  key: string
  from: string
  to: string
  slot: string
  /** `direct` from the record; `nested` from an expanded record. */
  kind: 'direct' | 'nested'
  /** SVG path data, in the placement's own units. */
  d: string
}

export interface LevelsPlacement {
  layout: 'levels'
  compact: boolean
  root: LevelsRect
  /** Every CARD, keyed by node key. A node is a card exactly once. */
  cards: Map<string, LevelsItem>
  refs: LevelsItem[]
  bands: LevelsBand[]
  wires: LevelsWire[]
  /** Where each loaded level's caption sits; level 0 is the record. Empty when compact. */
  columns: { level: number; x: number }[]
  bounds: { width: number; height: number }
}

export interface LevelsOptions {
  /** Stack the levels under the record instead of beside it. */
  compact?: boolean
  /** The width a compact arrangement fills. */
  width?: number
  /** A card's height. A card carrying a line of edge facts is taller. */
  cardHeight?: (key: string, via: string) => number
  /** Groups the legend has hidden: no band, no cards reached only through it. */
  hidden?: Iterable<string>
}

/** The geometry, named once so the renderer and the layout cannot disagree. */
export const LEVELS = {
  pad: 16,
  /** The row the column captions sit in. */
  caption: 28,
  rootWidth: 200,
  rootHeight: 88,
  compactRootHeight: 76,
  nodeWidth: 232,
  /** Between two columns — the room a wire curves in. */
  gap: 56,
  /** How far a band reaches left of the level-1 column, for its label. */
  bandInset: 16,
  bandHead: 40,
  bandFoot: 12,
  bandGap: 14,
  rowGap: 10,
  cardHeight: 56,
  refHeight: 30,
  moreHeight: 30,
  /** Compact only: the gutter the trunk runs in, and one level's indent. */
  trunk: 26,
  indent: 20,
} as const

/**
 * The narrowest canvas that holds the record and TWO levels side by side.
 *
 * Below it a host should ask for `compact`. The threshold is two levels rather
 * than one on purpose: at one, the first expansion lands off the right edge of
 * the column, and a click that visibly changes nothing is the "Graph not
 * working" report this surface has already had once.
 */
export const LEVELS_COMPACT_BELOW =
  LEVELS.pad * 2 + LEVELS.rootWidth + 2 * (LEVELS.gap + LEVELS.nodeWidth) + LEVELS.bandInset

const hopOf = (edge: GraphEdge): string => edge.key.slice(edge.key.lastIndexOf('@') + 1)

export function levelsLayout(model: GraphModel, options: LevelsOptions = {}): LevelsPlacement {
  const K = LEVELS
  const hidden = new Set(options.hidden ?? [])
  const heightOf = options.cardHeight ?? (() => K.cardHeight)
  const compact = options.compact ?? false
  const lines = lineIndexes(model.groups)
  const groups = model.groups.filter((g) => !hidden.has(g.slot))

  type Node = LevelsItem & { children: Node[] }
  const blank = { x: 0, y: 0, width: 0, height: 0 }
  const cardOf = new Map<string, Node>()
  const bandItems = new Map<string, Node[]>()
  const otherEnd = (edge: GraphEdge, end: string): string | null =>
    edge.source === end ? edge.target : edge.target === end ? edge.source : null

  const place = (into: Node[], key: string, level: number, band: string, parent: string, via: string) => {
    if (into.some((n) => n.key === key)) return
    const home = cardOf.get(key)
    if (home) {
      into.push({ ...blank, kind: 'ref', key, level, band, parent, via, home: home.band, children: [] })
      return
    }
    const card: Node = { ...blank, kind: 'card', key, level, band, parent, via, children: [] }
    cardOf.set(key, card)
    into.push(card)
  }

  // ── level 1: the record's own relationships, band by band ──────────────
  for (const group of groups) {
    const items: Node[] = []
    for (const edge of model.edges) {
      if (edge.slot !== group.slot || hopOf(edge) !== ROOT) continue
      const key = otherEnd(edge, ROOT)
      if (!key || key === ROOT || !model.nodes.has(key)) continue
      place(items, key, 1, group.slot, ROOT, edge.key)
    }
    bandItems.set(group.slot, items)
  }

  // ── level 2 and on: each expansion, once its own card exists ───────────
  // Taken in rounds rather than in `expanded` order, so a branch opened under a
  // node that itself arrived later still finds its parent.
  const pending = [...model.expanded]
  for (let progress = true; progress && pending.length > 0;) {
    progress = false
    for (let i = 0; i < pending.length; i += 1) {
      const at = pending[i]!
      const parent = cardOf.get(at)
      if (!parent) continue
      pending.splice(i, 1)
      i -= 1
      progress = true
      for (const edge of model.edges) {
        if (hopOf(edge) !== at) continue
        const key = otherEnd(edge, at)
        if (!key || key === at || key === ROOT || !model.nodes.has(key)) continue
        place(parent.children, key, parent.level + 1, parent.band, at, edge.key)
      }
    }
  }

  const heightOfItem = (n: Node) => (n.kind === 'ref' ? K.refHeight : heightOf(n.key, n.via))
  const all: Node[] = []
  const walk = (n: Node) => { all.push(n); n.children.forEach(walk) }
  for (const items of bandItems.values()) items.forEach(walk)
  const maxLevel = all.reduce((m, n) => Math.max(m, n.level), 1)

  const bands: LevelsBand[] = []
  let root: LevelsRect
  let width: number
  let bottom: number

  if (!compact) {
    // ── side by side: one column per level ───────────────────────────────
    const colX = (level: number) => K.pad + K.rootWidth + K.gap + (level - 1) * (K.nodeWidth + K.gap)
    const spans = new Map<Node, number>()
    const kidsSpan = (n: Node): number =>
      n.children.reduce((s, c) => s + spanOf(c), 0) + K.rowGap * Math.max(0, n.children.length - 1)
    const spanOf = (n: Node): number => {
      const known = spans.get(n)
      if (known !== undefined) return known
      const span = Math.max(heightOfItem(n), n.children.length ? kidsSpan(n) : 0)
      spans.set(n, span)
      return span
    }
    // A parent sits in the MIDDLE of its children, so a wire to the first and a
    // wire to the last are the same length — the design's own arrangement.
    const put = (n: Node, top: number) => {
      const span = spanOf(n)
      const own = heightOfItem(n)
      if (n.children.length) {
        let y = top + (span - kidsSpan(n)) / 2
        for (const c of n.children) { put(c, y); y += spanOf(c) + K.rowGap }
      }
      Object.assign(n, { x: colX(n.level), y: round(top + (span - own) / 2), width: K.nodeWidth, height: own })
    }

    const bandLeft = colX(1) - K.bandInset
    const bandWidth = colX(maxLevel) + K.nodeWidth + K.bandInset - bandLeft
    let y = K.pad + K.caption
    for (const group of groups) {
      const items = bandItems.get(group.slot) ?? []
      // A MEASURED zero with nothing loaded is quiet, as it is in List; the
      // legend and the accessible tree still name it. An unmeasured group is not
      // a zero and keeps its band.
      if (items.length === 0 && !group.more && group.count === 0) continue
      const top = y
      let at = top + K.bandHead
      for (const n of items) { put(n, at); at += spanOf(n) + K.rowGap }
      let more: LevelsRect | null = null
      let empty: LevelsRect | null = null
      if (group.more) {
        more = { x: colX(1), y: at, width: K.nodeWidth, height: K.moreHeight }
        at += K.moreHeight + K.rowGap
      } else if (items.length === 0) {
        empty = { x: colX(1), y: at, width: K.nodeWidth, height: K.refHeight }
        at += K.refHeight + K.rowGap
      }
      const end = at - K.rowGap + K.bandFoot
      bands.push({
        slot: group.slot, line: lines.get(group.slot) ?? 0,
        x: bandLeft, y: top, width: bandWidth, height: end - top, more, empty,
      })
      y = end + K.bandGap
    }

    // The record faces the middle of its first level.
    const firsts = all.filter((n) => n.level === 1).map((n) => n.y + n.height / 2)
    const top = K.pad + K.caption
    const mid = firsts.length ? (Math.min(...firsts) + Math.max(...firsts)) / 2 : top
    root = { x: K.pad, y: round(Math.max(top, mid - K.rootHeight / 2)), width: K.rootWidth, height: K.rootHeight }
    width = colX(maxLevel) + K.nodeWidth + K.bandInset + K.pad
    bottom = Math.max(bands.length ? y - K.bandGap : top, root.y + root.height)
  } else {
    // ── stacked: the record on top, each level indented under its parent ──
    width = Math.max(options.width ?? 0, K.pad * 2 + K.trunk + K.nodeWidth / 2)
    root = { x: K.pad, y: K.pad, width: width - K.pad * 2, height: K.compactRootHeight }
    const cardX = (level: number) => K.pad + K.trunk + (level - 1) * K.indent
    const cardW = (level: number) => width - K.pad - K.rowGap - cardX(level)
    let at = 0
    const put = (n: Node) => {
      const own = heightOfItem(n)
      Object.assign(n, { x: cardX(n.level), y: at, width: cardW(n.level), height: own })
      at += own + K.rowGap
      n.children.forEach(put)
    }
    let y = root.y + root.height + K.bandGap
    for (const group of groups) {
      const items = bandItems.get(group.slot) ?? []
      if (items.length === 0 && !group.more && group.count === 0) continue
      const top = y
      at = top + K.bandHead
      items.forEach(put)
      let more: LevelsRect | null = null
      let empty: LevelsRect | null = null
      if (group.more) {
        more = { x: cardX(1), y: at, width: cardW(1), height: K.moreHeight }
        at += K.moreHeight + K.rowGap
      } else if (items.length === 0) {
        empty = { x: cardX(1), y: at, width: cardW(1), height: K.refHeight }
        at += K.refHeight + K.rowGap
      }
      const end = at - K.rowGap + K.bandFoot
      bands.push({
        slot: group.slot, line: lines.get(group.slot) ?? 0,
        x: K.pad, y: top, width: width - K.pad * 2, height: end - top, more, empty,
      })
      y = end + K.bandGap
    }
    bottom = bands.length ? y - K.bandGap : root.y + root.height
  }

  // ── the wires: one per placing edge ─────────────────────────────────────
  const anchorOf = (key: string): LevelsRect => (key === ROOT ? root : cardOf.get(key)!)
  const slotOf = new Map(model.edges.map((e) => [e.key, e.slot] as const))
  const wires: LevelsWire[] = []
  for (const n of all) {
    const from = anchorOf(n.parent)
    const toY = round(n.y + n.height / 2)
    let d: string
    if (!compact) {
      const x1 = round(from.x + from.width)
      const y1 = round(from.y + from.height / 2)
      const xm = round((x1 + n.x) / 2)
      d = `M${x1} ${y1} C${xm} ${y1} ${xm} ${toY} ${n.x} ${toY}`
    } else {
      // An elbow down the parent's left gutter and into the card, its corner
      // rounded — the trunk the design's column wires become when stacked.
      const x1 = n.parent === ROOT ? K.pad + K.trunk / 2 : round(from.x + K.indent / 2)
      const y1 = round(from.y + from.height)
      const r = Math.min(8, Math.max(0, toY - y1))
      d = `M${x1} ${y1} V${round(toY - r)} Q${x1} ${toY} ${round(x1 + r)} ${toY} H${n.x}`
    }
    wires.push({
      key: n.via, from: n.parent, to: n.key,
      slot: slotOf.get(n.via) ?? n.band,
      kind: n.level === 1 ? 'direct' : 'nested', d,
    })
  }

  const strip = (n: Node): LevelsItem => ({
    kind: n.kind, key: n.key, level: n.level, band: n.band, parent: n.parent, via: n.via,
    ...(n.home ? { home: n.home } : {}),
    x: n.x, y: n.y, width: n.width, height: n.height,
  })
  const cards = new Map<string, LevelsItem>()
  for (const n of all) if (n.kind === 'card') cards.set(n.key, strip(n))
  return {
    layout: 'levels',
    compact,
    root,
    cards,
    refs: all.filter((n) => n.kind === 'ref').map(strip),
    bands,
    wires,
    columns: compact ? [] : [
      { level: 0, x: K.pad },
      ...Array.from({ length: maxLevel }, (_, i) => ({
        level: i + 1, x: K.pad + K.rootWidth + K.gap + i * (K.nodeWidth + K.gap),
      })),
    ],
    bounds: { width: round(width), height: round(bottom + K.pad) },
  }
}

/** Place the loaded graph. The only thing a layout choice may change. */
export function layoutGraph(
  model: GraphModel, layout: 'network' | 'metro', options: LayoutOptions = {},
): GraphPlacement {
  return layout === 'metro' ? metroLayout(model, options) : networkLayout(model, options)
}

/**
 * The same graph as a TREE — §7's "semantic accessible list/tree alternative
 * describing the same loaded graph".
 *
 * DERIVED from the model, never assembled separately (rule 42). A hand-built
 * alternative is a second description of the same thing, and the one nobody
 * looks at is the one that goes stale — which for an accessible alternative
 * means the people relying on it are the only ones seeing the old answer.
 */
export interface GraphOutlineGroup {
  slot: string
  label: string
  /** The caller's own count; `null` where the door did not measure it. */
  count: number | null
  capped: boolean
  more: boolean
  line: number
  nodes: { key: string; label: string | null; entity: string; depth: number; edgeKeys: string[] }[]
}

export function graphOutline(model: GraphModel): GraphOutlineGroup[] {
  const lines = lineIndexes(model.groups)
  const bySlot = new Map<string, Map<string, string[]>>()
  for (const edge of model.edges) {
    const key = edge.source === ROOT || model.expanded.includes(edge.source)
      ? edge.target : edge.source
    const slot = bySlot.get(edge.slot) ?? new Map<string, string[]>()
    slot.set(key, [...(slot.get(key) ?? []), edge.key])
    bySlot.set(edge.slot, slot)
  }
  return model.groups.map((group) => ({
    slot: group.slot,
    label: group.label,
    count: group.count,
    capped: group.capped,
    more: group.more,
    line: lines.get(group.slot) ?? 0,
    nodes: [...(bySlot.get(group.slot) ?? new Map<string, string[]>()).entries()]
      .map(([key, edgeKeys]) => {
        const node = model.nodes.get(key)
        return {
          key,
          label: node?.label ?? null,
          entity: node?.entity ?? '',
          depth: node?.depth ?? 1,
          edgeKeys,
        }
      })
      .sort((a, b) => a.depth - b.depth || a.key.localeCompare(b.key)),
  }))
}

/**
 * Which loaded nodes match a search, by their governed labels only.
 *
 * "Search within loaded nodes" (§7) and nothing wider: this component knows no
 * entity and can reach no door, so a search that appeared to look further would
 * be claiming a reach it does not have — the distinction the Connections surface
 * already draws between its in-page filter and the governed Add lookup.
 */
export function matchingNodes(model: GraphModel, query: string): Set<string> {
  const needle = query.trim().toLowerCase()
  if (!needle) return new Set(model.nodes.keys())
  const out = new Set<string>()
  for (const [key, node] of model.nodes) {
    if ((node.label ?? '').toLowerCase().includes(needle)) out.add(key)
  }
  return out
}
